import React, { useState, ReactNode, useEffect } from 'react'
import clsx from 'clsx'
import { useSessionStorage } from 'usehooks-ts'
import useIsAuth from 'sdk/hooks/useIsAuth';
import useLogout from 'sdk/hooks/useLogout';
import useCategories from 'sdk/hooks/useCategories';
import AuthModal from 'components/shared/modals/AuthModal';
import Tabs from 'components/shared/Tabs';
import MessageModal from 'components/shared/modals/MessageModal';
import { useNavigate } from 'react-router-dom';
import HomeView from './HomeView'
import PassView from './PassView'
import EmailView from './EmailView'
import PhoneView from './PhoneView'
import BirthdayView from './BirthdayView'
import NameView from './NameView'
import UsernameView from './UsernameView'
import CategoriesView from './CategoriesView'
import styles from './SignUp.module.scss';
import InterestsView from './InterestsView';
import GenderView from './GenderView';

enum SignMode { Phone, Email }
enum Steps {
  Home, PhoneEmail, Pass, Birthday, Names, Categories, TooYoung, Username, Success
}
const emailSteps = [
  Steps.PhoneEmail, Steps.Pass, Steps.Birthday, Steps.Categories, Steps.Username,
]
const phoneSteps = [
  Steps.PhoneEmail, Steps.Birthday, Steps.Categories, Steps.Username,
]

const ANIMATION_TIME = 300

function SignUp({
  onClose, goToSignIn, defaultStep, onBack,
}: {
  onClose: (shouldLogin?: boolean) => void,
  goToSignIn: () => void,
  defaultStep?: Steps,
  onBack?: () => void,
}) {
  const [, setShowModalStorage] = useSessionStorage<any>('showModal', null)
  const { data: isAuthData } = useIsAuth()
  const isAuth = isAuthData?.isAuth
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const categoriesData = useCategories()

  const [signMode, setSignMode] = useState<SignMode>(SignMode.Phone)
  const [step, setStep] = useState<Steps>(defaultStep ?? Steps.Home)
  const [email, setEmail] = useState('')
  const [animation, setAnimation] = useState('')

  const navigate = useNavigate()
  const logoutMutation = useLogout(navigate)

  useEffect(() => {
    if (defaultStep) setStep(defaultStep)
  }, [defaultStep]);

  const steps = (signMode === SignMode.Email) ? emailSteps : phoneSteps
  const curStep = steps.findIndex((v) => v === step)
  const navigateToStep = async (newStep: Steps, animations = true) => {
    if (newStep !== step) {
      if (animations && curStep !== steps.length - 1) {
        const isRev = newStep < step;
        const animationStyle = isRev ? styles.closeRev : styles.close;
        setAnimation(animationStyle);
        setTimeout(() => {
          setStep(newStep);
          setAnimation(isRev ? styles.openRev : styles.open);
          setTimeout(() => setAnimation(''), ANIMATION_TIME);
        }, ANIMATION_TIME);
      } else {
        setStep(newStep)
      }
    }
  }
  const goToNextStep = () => navigateToStep(steps[curStep + 1])
  const goToPrevStep = () => {
    navigateToStep(steps[curStep - 1])
    if (steps.length === curStep || curStep === 1) {
      onBack?.()
      logoutMutation.mutate()
    }
  }

  let content: ReactNode = null;

  let title = 'Sign up'

  if (step === Steps.Home && !isAuth) {
    return (
      <AuthModal
        onClose={() => onClose()}
        title="Sign up"
        showLogo
      >
        <HomeView
          className={clsx(styles.view, animation)}
          handleNext={() => navigateToStep(Steps.PhoneEmail)}
          goToSignIn={goToSignIn}
        />
      </AuthModal>
    )
  }

  const tabs = (
    <Tabs
      className={styles.tabs}
      items={[
        { title: 'Phone', id: SignMode.Phone },
        { title: 'Email', id: SignMode.Email },
      ]}
      onSelect={setSignMode}
      value={signMode}
      showNoCheck
      white
      inline
    />
  )
  if (step === Steps.PhoneEmail) {
    const commonProps = {
      onClose: () => onClose(),
      onBack: () => navigateToStep(Steps.Home),
      title: 'Sign up',
      showLogo: true,
      tabs,
    };
    if ((signMode === SignMode.Email)) {
      return (
        <AuthModal
          {...commonProps}
        >
          {tabs}
          <EmailView
            className={clsx(styles.view, animation)}
            email={email}
            setEmail={setEmail}
            handleNext={() => navigateToStep(Steps.Pass)}
            goToSignIn={goToSignIn}
          />
        </AuthModal>
      )
    }
    if ((signMode === SignMode.Phone)) {
      return (
        <AuthModal
          {...commonProps}
        >
          {tabs}
          <PhoneView
            className={clsx(styles.view, animation)}
            handleNext={() => navigateToStep(Steps.Birthday)}
            goToSignIn={goToSignIn}
          />
        </AuthModal>
      )
    }
  }
  if (step === Steps.Pass) {
    return (
      <AuthModal
        onClose={() => onClose()}
        onBack={goToPrevStep}
        title="Sign up"
        showLogo
      >
        <PassView
          className={clsx(styles.view, animation)}
          handleNext={() => navigateToStep(Steps.Birthday)}
          goToSignIn={goToSignIn}
          email={email}
        />
      </AuthModal>
    )
  }

  if (step === Steps.Success) {
    return (
      <MessageModal
        title="Success"
        status="success"
        onClick={() => {
          navigate("/profile")
          onClose(true)
        }}
        message="You have successfully created your account"
        medium
        showCloseBtn
      />
    )
  }
  if (step === Steps.Birthday) {
    return (
      <AuthModal
        onClose={() => onClose()}
        onBack={goToPrevStep}
        title="Sign up"
        showLogo
      >
        <BirthdayView
          className={clsx(styles.view, animation)}
          handleNext={() => navigateToStep(Steps.Categories)}
          tooYoung={(message: string) => {
            setShowModalStorage({
              type: 'sorry',
              title: 'Sorry',
              message,
            })
            onClose()
          }}
        />
      </AuthModal>
    )
  }

  switch (step) {
    case Steps.Names:
      content = (
        <NameView
          className={clsx(styles.view, animation)}
          stepsCount={steps.length}
          curStep={curStep}
          handleNext={goToNextStep}
        />
      );
      break;

    case Steps.Username:
      content = (
        <UsernameView
          className={clsx(styles.view, animation)}
          handleNext={() => navigateToStep(Steps.Success)}
        />
      );
      title = '';
      break;

    case Steps.Categories:
      content = (
        <GenderView
          className={clsx(styles.view,)}
          handleNext={() => navigateToStep(Steps.Username)}
        />
      );
      title = '';
      break;
    default:
      break;
  }

  return (
    <AuthModal
      onClose={() => onClose()}
      onBack={goToPrevStep}
      title={title}
      showLogo
    >
      {content}
    </AuthModal>
  )
}

export default SignUp
